import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationConfigClient } from '@/lib/api/integration-config-client';

/** One valid external category value. Mirrors the backend ExternalCategoryOption. */
export interface ExternalCategoryOption {
  value: string;
  label: string;
  sub_categories?: ExternalCategoryOption[] | null;
}

/** Per-provider config blob. Known sections are typed; unknown keys are preserved. */
export interface IntegrationConfigBody {
  external_categories?: ExternalCategoryOption[] | null;
  tags?: Record<string, string> | null;
  [key: string]: unknown;
}

export interface IntegrationConfigRead {
  id: string;
  provider: string;
  config: IntegrationConfigBody;
}

export interface CredentialSummary {
  id: string;
  name: string;
  provider: string;
  category: string;
  label?: string | null;
  enabled: boolean;
}

interface CredentialsListResponse {
  credentials: CredentialSummary[];
  total: number;
}

// Scope a request to a target tenant. `tenant_id` query is consumed by the
// backoffice-api proxy (REMOTE); `x-tenant-id` header is used by the
// integrations service directly (LOCAL). Sending both is safe in either mode.
const tenantScope = (tenantId: string) => ({
  params: { tenant_id: tenantId },
  headers: { 'x-tenant-id': tenantId },
});

export function useIntegrationCredentials(tenantId: string) {
  return useQuery({
    queryKey: ['integration-credentials', tenantId],
    queryFn: async () => {
      const response = await integrationConfigClient.get<CredentialsListResponse>(
        '/credentials',
        tenantScope(tenantId),
      );
      return response.data.credentials;
    },
    enabled: !!tenantId,
  });
}

export const integrationConfigQueryKey = (tenantId: string, credentialId: string | null) =>
  ['integration-config', tenantId, credentialId] as const;

export async function fetchIntegrationConfig(
  tenantId: string,
  credentialId: string,
): Promise<IntegrationConfigRead> {
  const response = await integrationConfigClient.get<IntegrationConfigRead>(
    `/credentials/${credentialId}/config`,
    tenantScope(tenantId),
  );
  return response.data;
}

export function useIntegrationConfig(tenantId: string, credentialId: string | null) {
  return useQuery({
    queryKey: integrationConfigQueryKey(tenantId, credentialId),
    queryFn: () => {
      if (!credentialId) throw new Error('credentialId is required');
      return fetchIntegrationConfig(tenantId, credentialId);
    },
    enabled: !!tenantId && !!credentialId,
  });
}

export function useUpdateIntegrationConfig(tenantId: string, credentialId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (config: IntegrationConfigBody) => {
      const response = await integrationConfigClient.put<IntegrationConfigRead>(
        `/credentials/${credentialId}/config`,
        { config },
        tenantScope(tenantId),
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: integrationConfigQueryKey(tenantId, credentialId),
      });
    },
  });
}
